import { IPagination } from '../types/pagination';
import { ITotpConfig } from '../types/user';
import { IDepartment } from '../views/settings/types/department';
import { IEmployee, IInvolvementEmployees } from '../views/settings/types/employee';
import { IRight } from '../views/settings/types/right';
import { IRole } from '../views/settings/types/role';
import { ICompany, IStandardSetting } from './../views/settings/types/company';
import { IPositionBillingRate } from './../views/settings/types/department';
import { HttpClient } from './service';
import Urls from './urls';

export const UserSettingsApi = {
  /* Departments */
  getDepartments(queryParams?: Record<string, string>): Promise<IDepartment[]> {
    return HttpClient.get<IDepartment[], Record<string, string>>(Urls.UserSettings.departments, queryParams)
  },

  getPaginatedDepartments(queryParams?: Record<string, string>): Promise<IPagination<IDepartment[]>> {
    return HttpClient.get<IPagination<IDepartment[]>, Record<string, string>>(Urls.UserSettings.departments, queryParams)
  },

  getDepartment(id: string | number): Promise<IDepartment> {
    return HttpClient.get<IDepartment, null>(`${Urls.UserSettings.departments}${id}/`)
  },

  deleteDepartment(id: string | number): Promise<string | number> {
    return HttpClient.delete<string | number, string | number>(Urls.UserSettings.departments, id)
  },

  createDepartment(department: IDepartment): Promise<IDepartment> {
    return HttpClient.post<IDepartment, IDepartment>(Urls.UserSettings.departments, department)
  },

  updateDepartment(department: IDepartment): Promise<IDepartment> {
    return HttpClient.put<IDepartment, IDepartment>(Urls.UserSettings.departments, department.id, department)
  },

  /* Employees */

  setEmailNotifications(userId: number, emailNotifications: boolean) {
    return HttpClient.post<IEmployee, { id: number, emailNotifications: boolean }>(Urls.UserUrls.emailNotifications, { id: userId, emailNotifications} );
  },

  setPassword(userId: number, password: string, old_password: string): Promise<null> {
    return HttpClient.post<null, { id: number, password: string, old_password: string }>(Urls.UserSettings.setPasswordPath, { id: userId, password, old_password} )
  },

  getTotpConfiguration(userId: number): Promise<ITotpConfig> {
    return HttpClient.get<ITotpConfig, { id: number }>(Urls.UserUrls.totpConfigurationPath, { id: userId })
  },

  getEmployees(queryParams?: Record<string, string>): Promise<IEmployee[]> {
    return HttpClient.get<IEmployee[], Record<string, string>>(Urls.UserSettings.employees, queryParams)
  },

  getPaginatedEmployees(queryParams?: Record<string, string>): Promise<IPagination<IEmployee[]>> {
    return HttpClient.get<IPagination<IEmployee[]>, Record<string, string>>(Urls.UserSettings.employees, queryParams)
  },

  getEmployee(id: string | number): Promise<IEmployee> {
    return HttpClient.get<IEmployee, null>(`${Urls.UserSettings.employees}${id}/`)
  },

  getOpenProjectEmployees(prId: string | number): Promise<IEmployee[]> {
    return HttpClient.get<IEmployee[], null>(`${Urls.UserSettings.employees}${prId}/booked-employees/`)
  },

  getPaginatedOpenProjectEmployees(prId: string | number, queryParams?: Record<string, string>): Promise<IPagination<IEmployee[]>> {
    return HttpClient.get<IPagination<IEmployee[]>, Record<string, string>>(`${Urls.UserSettings.employees}${prId}/booked-employees/`, queryParams)
  },

  getInvolevedEmployees(prId: string | number, queryParams: Record<string, string>): Promise<IInvolvementEmployees[]> {
    return HttpClient.get<IInvolvementEmployees[], Record<string, string>>(`${Urls.UserSettings.employees}${prId}/members-involvement/`, queryParams)
  },

  getPaginatedInvolevedEmployees(prId: string | number, queryParams: Record<string, string>): Promise<IPagination<IInvolvementEmployees[]>> {
    return HttpClient.get<IPagination<IInvolvementEmployees[]>, Record<string, string>>(`${Urls.UserSettings.employees}${prId}/members-involvement/`, queryParams)
  },

  deleteEmployee(id: string | number): Promise<string | number> {
    return HttpClient.delete<string | number, string | number>(Urls.UserSettings.employees, id)
  },

  createEmployee(employee: IEmployee): Promise<IEmployee> {
    return HttpClient.post<IEmployee, IEmployee>(Urls.UserSettings.employees, employee)
  },

  updateEmployee(employee: IEmployee): Promise<IEmployee> {
    return HttpClient.put<IEmployee, IEmployee>(Urls.UserSettings.employees, employee.id, employee)
  },

  updateEmployeePhoto(employee: IEmployee, file?: File): Promise<IEmployee> {
    return HttpClient.uploadFile<IEmployee>(`${Urls.UserSettings.employees}${employee.id}/employees-upload-image/`, file)
  },

  setEmployeesPositions(employees: IEmployee[]): Promise<IEmployee[]> {
    return HttpClient.put<IEmployee[], IEmployee[]>(`${Urls.UserSettings.employees}positions/`, undefined, employees)
  },

  removeEmployeePhoto(id: string | number): Promise<string | number> {
    return HttpClient.delete<string | number, string | number>(`${Urls.UserSettings.employees}${id}/employees-remove-image`, '')
  },

  /* Company */
  getCompanies(queryParams?: Record<string, string>): Promise<ICompany[]> {
    return HttpClient.get<ICompany[], Record<string, string>>(Urls.UserSettings.company, queryParams)
  },

  getCompany(id: string | number): Promise<ICompany> {
    return HttpClient.get<ICompany, null>(`${Urls.UserSettings.company}${id}/`)
  },

  createCompany(company: ICompany): Promise<ICompany> {
    return HttpClient.post<ICompany, ICompany>(Urls.UserSettings.company, company)
  },

  updateCompany(company: ICompany): Promise<ICompany> {
    return HttpClient.put<ICompany, ICompany>(Urls.UserSettings.company, company.id, company)
  },

   /* Standard Setting */
   getStandardSettings(queryParams?: Record<string, string>): Promise<IStandardSetting[]> {
    return HttpClient.get<IStandardSetting[], Record<string, string>>(Urls.UserSettings.standardSetting, queryParams)
  },

   getStandardSetting(id: string | number): Promise<IStandardSetting> {
    return HttpClient.get<IStandardSetting, null>(`${Urls.UserSettings.standardSetting}${id}/`)
  },

  createStandardSetting(standardSetting: IStandardSetting): Promise<IStandardSetting> {
    return HttpClient.post<IStandardSetting, IStandardSetting>(Urls.UserSettings.standardSetting, standardSetting)
  },

  updateStandardSetting(standardSetting: IStandardSetting): Promise<IStandardSetting> {
    return HttpClient.put<IStandardSetting, IStandardSetting>(Urls.UserSettings.standardSetting, standardSetting.id, standardSetting)
  },

  /* Roles */
  getRoles(queryParams?: Record<string, string>): Promise<IRole[]> {
    return HttpClient.get<IRole[], Record<string, string>>(Urls.RoleUrls.roles, queryParams)
  },

  getPaginatedRoles(queryParams?: Record<string, string>): Promise<IPagination<IRole[]>> {
    return HttpClient.get<IPagination<IRole[]>, Record<string, string>>(Urls.RoleUrls.roles, queryParams)
  },

  getRole(id: string | number): Promise<IRole> {
    return HttpClient.get<IRole, null>(`${Urls.RoleUrls.roles}${id}/`)
  },

  deleteRole(id: string | number): Promise<string | number> {
    return HttpClient.delete<string | number, string | number>(Urls.RoleUrls.roles, id)
  },

  createRole(role: IRole): Promise<IRole> {
    return HttpClient.post<IRole, IRole>(Urls.RoleUrls.roles, role)
  },

  updateRole(role: IRole): Promise<IRole> {
    return HttpClient.put<IRole, IRole>(Urls.RoleUrls.roles, role.id, role)
  },

  getRights(queryParams?: Record<string, string>): Promise<IRight[]> {
    return HttpClient.get<IRight[], Record<string, string>>(Urls.RoleUrls.rights, queryParams)
  },

  /* Position Billing Rate */
  deleteBillingRate(id: string | number): Promise<string | number> {
    return HttpClient.delete<string | number, string | number>(Urls.UserSettings.positionBillingRate, id)
  },

  updateBillingRate(posBillingRate: IPositionBillingRate): Promise<IPositionBillingRate> {
    return HttpClient.put<IPositionBillingRate, IPositionBillingRate>(Urls.UserSettings.positionBillingRate, posBillingRate.id, posBillingRate)
  },

  createBillingRate(posBillingRate: IPositionBillingRate): Promise<IPositionBillingRate> {
    return HttpClient.post<IPositionBillingRate, IPositionBillingRate>(Urls.UserSettings.positionBillingRate, posBillingRate)
  }
}
